import React, { createContext, useContext, useEffect, useState } from 'react'
import { useDispatch } from '../../../store'
import { canvasAuthService } from '../../../common/services/CanvasAuthService'
import { canvasSdkService } from '../services/CanvasSdkService'
import type { SalesforceContextData } from '../types/salesforce'
import { isEmpty } from 'lodash'
import { hydrateAuthorizationClaims } from '../../../common/modules/authorization'
import { getClaimsFromToken } from '../../../common/utils/helperFunctions'
import { getAccount, PartnerType } from '../../../common/modules/partner/action'
import { loadAvailableAccounts } from '../../../common/modules/accountSelection/action'
import { useUpdateConfigFlags } from '@labrav/flags'
import {
  markExternalFlagsConfigured,
  isExternalFlagsConfigured,
} from '../../../common/utils/Hooks/partner'
import { PasswordManagerGuard } from '../../../common/components/PasswordManagerGuard/PasswordManagerGuard'
import {
  EXCHANGE_TOKEN_ABSENT_CLIENT,
  getCanvasAuthErrorFromLocation,
  getCanvasAuthErrorMessage,
  isCanvasAuthErrorCode,
  shouldAttemptCanvasAuth,
} from '../utils/canvasAuthErrors'
import { isHubspotExternalAppPath } from '../../../common/utils/hubspotExternalAppPaths'

const SalesforceContext = createContext<SalesforceContextData | undefined>(
  undefined
)

interface SalesforceContextProviderProps {
  children: React.ReactNode
}

export const SalesforceContextProvider: React.FC<
  SalesforceContextProviderProps
> = ({ children }) => {
  const dispatch = useDispatch()
  const { setConfig: setFlagsConfig } = useUpdateConfigFlags()
  const [contextData, setContextData] = useState<SalesforceContextData>({
    partnerId: '',
    record: undefined,
    organization: { Id: '' },
    actions: {
      openModal: () => {},
      navigateToUrl: () => null,
      navigateToRecord: () => null,
      navigateBack: () => {},
      refreshParent: () => {},
      closeModal: () => {},
      resize: () => {},
      autogrow: () => {},
      triggerAction: () => {},
    },
    crmId: '',
    scopes: [],
    customParameters: undefined,
    isAuthInitialized: false,
    authError: null,
    loading: true, // Start with loading: true
    error: undefined,
    isInSalesforceContext: false,
  })

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      isHubspotExternalAppPath(window.location.pathname)
    ) {
      setContextData(prev => ({
        ...prev,
        loading: false,
        isInSalesforceContext: false,
      }))
      return
    }
    let cancelled = false

    const initializeAuth = async () => {
      // SalesforceContextProvider wraps the whole Auth0 app — only run Canvas
      // JWT exchange / handshake-error handling when we are actually in Canvas.
      if (
        !shouldAttemptCanvasAuth({
          hasExchangeToken:
            !!canvasAuthService.getExchangeTokenFromSessionStorage(),
        })
      ) {
        if (cancelled) return
        setContextData(prev => ({
          ...prev,
          isAuthInitialized: false,
          authError: null,
          loading: false,
          error: 'salesforce_context_not_available',
          isInSalesforceContext: false,
        }))
        return
      }

      // Handshake failure redirects land here with ?canvas_auth_error=...
      // Skip /token exchange — sessionStorage will be empty and would only
      // produce a noisy exchange_jwt_missing 401.
      const handshakeError = getCanvasAuthErrorFromLocation()
      if (handshakeError) {
        if (cancelled) return
        setContextData(prev => ({
          ...prev,
          isAuthInitialized: false,
          authError: getCanvasAuthErrorMessage(handshakeError),
          loading: false,
          error: handshakeError,
          isInSalesforceContext: false,
        }))
        return
      }

      try {
        await canvasAuthService.initialize()
        if (cancelled) return

        const partnerId = canvasAuthService.getPartnerId()
        const record = canvasAuthService.getRecord()
        const organization = canvasAuthService.getOrganization()
        const crmId = canvasAuthService.getCrmId()
        const customParameters = canvasAuthService.getCustomParameters()
        const accessToken = canvasAuthService.getAccessToken()
        const scopes = canvasAuthService.getScopes()

        // Pre-fetch partner data, accounts, and flags as soon as the canvas JWT is
        // decoded — before loading:false triggers a re-render and route components
        // mount. Guards in getAccount / loadAvailableAccounts ensure only the first
        // call (here) actually hits the network; all later callers skip.
        if (partnerId) {
          dispatch(getAccount(partnerId, PartnerType.User))
          dispatch(loadAvailableAccounts(partnerId))
          // sync_configurations are account-scoped; fetch in ReferralFormWrapper
          // (and similar) once the OPPSYNC cloud account id is known — not tenant partnerId.
          if (!isExternalFlagsConfigured(partnerId)) {
            setFlagsConfig({ user: { partnerId } })
            markExternalFlagsConfigured(partnerId)
          }
          // Prefetch the most common route chunks in parallel with the API calls
          // so the lazy boundary resolves from browser cache when the route renders.
          import('../wrappers/ActionHubWrapper').catch(() => {})
          import('../wrappers/ReferralFormWrapper').catch(() => {})
        }

        const claimsFromToken = (() => {
          if (!accessToken) return { roles: [], scopes: [] }
          try {
            const claims = getClaimsFromToken(accessToken)
            return { roles: claims.roles, scopes: claims.scopes }
          } catch {
            return { roles: [], scopes: [] }
          }
        })()

        dispatch(
          hydrateAuthorizationClaims({
            source: 'canvas',
            partnerId: partnerId || null,
            roles: claimsFromToken.roles,
            scopes,
            isAuthenticated: true,
            claimsLoaded: true,
          })
        )

        setContextData({
          partnerId: partnerId || '',
          record,
          organization: organization || { Id: '' },
          actions: {
            openModal: canvasSdkService.openModal.bind(canvasSdkService),
            navigateToUrl:
              canvasSdkService.navigateToUrl.bind(canvasSdkService),
            navigateToRecord:
              canvasSdkService.navigateToRecord.bind(canvasSdkService),
            navigateBack: canvasSdkService.navigateBack.bind(canvasSdkService),
            refreshParent:
              canvasSdkService.refreshParent.bind(canvasSdkService),
            closeModal: canvasSdkService.closeModal.bind(canvasSdkService),
            resize: canvasSdkService.resize.bind(canvasSdkService),
            autogrow: canvasSdkService.autogrow.bind(canvasSdkService),
            triggerAction:
              canvasSdkService.triggerAction.bind(canvasSdkService),
          },
          crmId: crmId || '',
          scopes,
          customParameters: customParameters || undefined,
          isAuthInitialized: true,
          authError: null,
          loading: false, // Loading complete
          error: undefined,
          isInSalesforceContext: true,
        })
      } catch (error) {
        if (cancelled) return

        const errorCode =
          error instanceof Error &&
          error.message === EXCHANGE_TOKEN_ABSENT_CLIENT
            ? EXCHANGE_TOKEN_ABSENT_CLIENT
            : 'salesforce_context_not_available'
        const messageKey = error instanceof Error ? error.message : errorCode
        // Known codes map to user copy; unknown messages still get a safe
        // fallback, but keep the original on the console for debugging.
        if (
          messageKey !== EXCHANGE_TOKEN_ABSENT_CLIENT &&
          !isCanvasAuthErrorCode(messageKey)
        ) {
          console.error('Salesforce Canvas auth initialization failed', {
            error,
            cause: error instanceof Error ? error.cause : undefined,
          })
        }
        setContextData(prev => ({
          ...prev,
          isAuthInitialized: false,
          authError: getCanvasAuthErrorMessage(messageKey),
          loading: false, // Loading complete (with error)
          error: errorCode,
          isInSalesforceContext: false,
        }))
      }
    }

    initializeAuth()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const handler = () => {
      const accessToken = canvasAuthService.getAccessToken()
      const scopes = canvasAuthService.getScopes()

      const roles = (() => {
        if (!accessToken) return []
        try {
          const claims = getClaimsFromToken(accessToken)
          return claims.roles || []
        } catch {
          return []
        }
      })()

      dispatch(
        hydrateAuthorizationClaims({
          source: 'canvas',
          partnerId: canvasAuthService.getPartnerId(),
          roles,
          scopes,
          isAuthenticated: true,
          claimsLoaded: true,
        })
      )

      setContextData(prev => ({ ...prev, scopes }))
    }

    window.addEventListener('canvas-auth-updated', handler)
    return () => window.removeEventListener('canvas-auth-updated', handler)
  }, [dispatch])

  return (
    <SalesforceContext.Provider value={contextData}>
      <PasswordManagerGuard enabled={contextData.isInSalesforceContext}>
        {children}
      </PasswordManagerGuard>
    </SalesforceContext.Provider>
  )
}

export const useSalesforceContext = (): SalesforceContextData => {
  const context = useContext(SalesforceContext)

  if (context === undefined || isEmpty(context.partnerId)) {
    return {
      partnerId: '',
      record: undefined,
      organization: { Id: '' },
      actions: {
        openModal: () => {},
        navigateToUrl: () => null,
        navigateToRecord: () => null,
        navigateBack: () => {},
        refreshParent: () => {},
        closeModal: () => {},
        resize: () => {},
        autogrow: () => {},
        triggerAction: () => {},
      },
      crmId: '',
      scopes: [],
      customParameters: undefined,
      error: context?.error ?? 'salesforce_context_not_available',
      loading: context?.loading ?? false,
      isAuthInitialized: context?.isAuthInitialized ?? false,
      authError: context?.authError ?? null,
      isInSalesforceContext: false,
    }
  }

  return context
}
